import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  MessageSquare,
  Eye,
  Receipt,
  CreditCard
} from 'lucide-react';
import { useGst } from '../../context/GstContext';
import { MerchantInvoice, InvoicePaymentStatus } from '../../types/gst';
import { InvoiceViewerModal } from '../../components/billing/InvoiceViewerModal';
import { RecordPaymentModal } from '../../components/billing/RecordPaymentModal';
import { InvoiceMetricsRow } from '../../components/billing/InvoiceMetricsRow';
import { generateInvoiceWhatsAppLink } from '../../services/billingService';
import { toast } from '../../components/ui';

export const InvoiceListPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    merchantInvoices,
    billingSettings,
    updateMerchantInvoice,
    saveBillingSettings,
    financialYear
  } = useGst();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | InvoicePaymentStatus>('ALL');
  const [selectedInvoice, setSelectedInvoice] = useState<MerchantInvoice | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  // Manual Payment Recording Modal State
  const [paymentModalOpen, setPaymentModalOpen] = useState<boolean>(false);
  const [paymentInvoice, setPaymentInvoice] = useState<MerchantInvoice | null>(null);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState<boolean>(false);

  const filteredInvoices = merchantInvoices.filter(inv => {
    const matchesSearch =
      (inv.invoiceNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.customerGstin || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'ALL' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleOpenPreview = (inv: MerchantInvoice) => {
    setSelectedInvoice(inv);
    setPreviewModalOpen(true);
  };

  const handleWhatsApp = (inv: MerchantInvoice) => {
    const link = generateInvoiceWhatsAppLink(inv, billingSettings || undefined);
    window.open(link, '_blank');
    toast.success(`WhatsApp opened for invoice #${inv.invoiceNumber}`);
  };

  const handleOpenPaymentModal = (inv: MerchantInvoice) => {
    setPaymentInvoice(inv);
    setPaymentModalOpen(true);
  };

  const handleRecordPayment = async (
    paymentAmount: number,
    paymentMode: 'CASH' | 'UPI' | 'NET_BANKING' | 'CHEQUE',
    paymentRefNumber: string,
    paymentDate: string
  ) => {
    if (!paymentInvoice) return;
    setIsSubmittingPayment(true);
    try {
      const currentPaid = paymentInvoice.paidAmount || 0;
      const newPaidTotal = Math.min(paymentInvoice.grandTotal, currentPaid + paymentAmount);
      const newBalanceDue = Math.max(0, paymentInvoice.grandTotal - newPaidTotal);
      const newStatus: InvoicePaymentStatus = newBalanceDue <= 0 ? 'PAID' : 'PARTIALLY_PAID';

      const updated: MerchantInvoice = {
        ...paymentInvoice,
        paidAmount: newPaidTotal,
        balanceDue: newBalanceDue,
        status: newStatus,
        paymentMode,
        notes: paymentInvoice.notes
          ? `${paymentInvoice.notes}\n[Payment Recorded]: ₹${paymentAmount} via ${paymentMode} (Ref: ${paymentRefNumber || 'N/A'}) on ${paymentDate}`
          : `[Payment Recorded]: ₹${paymentAmount} via ${paymentMode} (Ref: ${paymentRefNumber || 'N/A'}) on ${paymentDate}`,
        updatedAt: new Date().toISOString()
      };

      await updateMerchantInvoice(updated);
      toast.success(`Recorded ₹${paymentAmount.toLocaleString('en-IN')} payment for #${paymentInvoice.invoiceNumber}`);
      setPaymentModalOpen(false);
    } catch (err: any) {
      toast.error('Failed to record payment: ' + err.message);
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Page Title & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Receipt className="h-6 w-6 text-cyan-400" />
            <span>Merchant Billing & Everyday Invoicing</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            High-speed merchant billing with standard statutory GST templates, WhatsApp delivery & E-Invoice integration
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/billing/templates"
            className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 px-3.5 py-2 text-xs font-semibold transition"
          >
            Invoice Templates
          </Link>
          <Link
            to="/billing/create"
            className="flex items-center gap-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 text-xs font-bold transition shadow-sm shadow-cyan-500/25"
          >
            <Plus className="h-4 w-4" />
            <span>Create Invoice</span>
          </Link>
        </div>
      </div>

      {/* KPI Metric Cards */}
      <InvoiceMetricsRow invoices={merchantInvoices} financialYear={financialYear} />

      {/* Filter and Search Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#131316] p-3 rounded-2xl border border-white/10 text-xs">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by invoice #, customer name, GSTIN..."
            className="w-full rounded-xl border border-white/10 bg-[#0e1015] pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto">
          {[
            { key: 'ALL', label: 'All Invoices' },
            { key: 'ISSUED', label: 'Issued / Unpaid' },
            { key: 'PARTIALLY_PAID', label: 'Partially Paid' },
            { key: 'PAID', label: 'Paid in Full' },
            { key: 'OVERDUE', label: 'Overdue' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key as any)}
              className={`px-3 py-1.5 rounded-xl font-medium transition ${
                statusFilter === tab.key
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Invoices Table */}
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#131316]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#0e1015] text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-white/10">
            <tr>
              <th className="py-3 px-4">Invoice # & Date</th>
              <th className="py-3 px-4">Customer</th>
              <th className="py-3 px-4 text-right">Taxable (₹)</th>
              <th className="py-3 px-4 text-right">GST (₹)</th>
              <th className="py-3 px-4 text-right">Grand Total (₹)</th>
              <th className="py-3 px-4 text-right">Balance Due (₹)</th>
              <th className="py-3 px-4 text-center">Status</th>
              <th className="py-3 px-4 text-center">Compliance</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono text-slate-300">
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-12 text-center text-slate-500 font-sans">
                  No invoices found. Click <strong>Create Invoice</strong> or load sample data.
                </td>
              </tr>
            ) : (
              filteredInvoices.map(inv => (
                <tr key={inv.id} className="hover:bg-white/[0.02] transition">
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleOpenPreview(inv)}
                      className="font-bold text-cyan-400 hover:underline text-left"
                    >
                      {inv.invoiceNumber}
                    </button>
                    <div className="text-[10px] text-slate-500 font-normal">{inv.invoiceDate}</div>
                  </td>

                  <td className="py-3 px-4 font-sans">
                    <div className="font-semibold text-white">{inv.customerName}</div>
                    <div className="font-mono text-[10px] text-slate-400">
                      {inv.customerGstin || 'Unregistered B2C'}
                    </div>
                  </td>

                  <td className="py-3 px-4 text-right font-medium">
                    ₹{inv.subtotalTaxable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>

                  <td className="py-3 px-4 text-right text-cyan-300">
                    ₹{inv.totalTax.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>

                  <td className="py-3 px-4 text-right font-bold text-white">
                    ₹{inv.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>

                  <td className="py-3 px-4 text-right font-bold">
                    {inv.balanceDue > 0 ? (
                      <span className="text-amber-400">₹{inv.balanceDue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    ) : (
                      <span className="text-emerald-400">₹0.00</span>
                    )}
                  </td>

                  <td className="py-3 px-4 text-center font-sans">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${
                        inv.status === 'PAID'
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : inv.status === 'OVERDUE'
                          ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                          : inv.status === 'PARTIALLY_PAID'
                          ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                          : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      }`}
                    >
                      {inv.status}
                    </span>
                  </td>

                  <td className="py-3 px-4 text-center font-sans">
                    <div className="flex items-center justify-center gap-1.5">
                      {inv.eInvoice?.status === 'GENERATED' ? (
                        <span className="rounded bg-cyan-950/60 border border-cyan-500/30 text-cyan-300 px-1.5 py-0.5 text-[9px] font-mono font-bold" title="E-Invoice IRN Generated">
                          IRN ✅
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-600">—</span>
                      )}
                      {inv.eWayBill?.status === 'GENERATED' && (
                        <span className="rounded bg-emerald-950/60 border border-emerald-500/30 text-emerald-300 px-1.5 py-0.5 text-[9px] font-mono font-bold" title="E-Way Bill Generated">
                          EWB 🚛
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="py-3 px-4 text-right font-sans">
                    <div className="flex items-center justify-end gap-1.5">
                      {inv.balanceDue > 0 && (
                        <button
                          onClick={() => handleOpenPaymentModal(inv)}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-[10px] font-semibold border border-emerald-500/30 transition shadow-sm"
                          title="Record partial or full payment"
                        >
                          <CreditCard className="h-3 w-3 text-emerald-400" />
                          <span>Record Pay</span>
                        </button>
                      )}

                      <button
                        onClick={() => handleWhatsApp(inv)}
                        className="p-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30 transition"
                        title="1-Click WhatsApp Invoice Dispatch"
                      >
                        <MessageSquare className="h-3.5 w-3.5 text-emerald-400" />
                      </button>

                      <button
                        onClick={() => handleOpenPreview(inv)}
                        className="p-1.5 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white transition"
                        title="View / Print Invoice"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Manual Payment Recording Modal */}
      <RecordPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        invoice={paymentInvoice}
        onConfirmPayment={handleRecordPayment}
        isSubmitting={isSubmittingPayment}
      />

      {/* Invoice Viewer / Print Modal */}
      <InvoiceViewerModal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        invoice={selectedInvoice}
        settings={billingSettings || undefined}
        onUpdateTemplate={async (templateId) => {
          if (billingSettings) {
            await saveBillingSettings({ ...billingSettings, defaultTemplateId: templateId });
          }
        }}
      />
    </div>
  );
};
